/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import { ADERE_KNOWN_STATUS_KEYS, VENCIMENTO_MODES } from './constants';

export const CHART_HEIGHT = 320;
export const VENCIMENTO_CHART_HEIGHT = 220;

const ISO_DATE = 'YYYY-MM-DD';

const isPlainObject = (value) =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const normalizeAdereCounts = (item) => {
  if (!item || typeof item !== 'object') return {};
  if (isPlainObject(item.counts)) return item.counts;
  if (isPlainObject(item.statusCounts)) return item.statusCounts;

  const flat = {};
  ADERE_KNOWN_STATUS_KEYS.forEach((status) => {
    if (item[status] != null) {
      flat[status] = item[status];
    }
  });
  return flat;
};

const normalizeAdereItem = (item) => {
  if (!item || typeof item !== 'object') return null;
  const counts = normalizeAdereCounts(item);
  const totalFromCounts = Object.values(counts).reduce(
    (acc, value) => acc + (Number(value) || 0),
    0,
  );

  return {
    adereMonth: item.adereMonth ?? item.adere ?? item.month ?? null,
    malhaId: item.malhaId ?? item.malha?.id ?? null,
    malhaNome:
      item.malhaNome
      ?? item.malhaName
      ?? (typeof item.malha === 'string' ? item.malha : item.malha?.nome ?? null),
    total: item.total ?? item.nsirs ?? item.count ?? totalFromCounts,
    counts,
  };
};

/** Normaliza body axios + variantes de payload do backend. */
export const normalizeAdereChartResponse = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return { items: [], statusOrder: null };
  }

  const body = isPlainObject(payload.data) && !Array.isArray(payload.data)
    ? payload.data
    : payload;

  const rawItems = body.items ?? body.rows ?? body.points ?? body.content ?? body.data ?? [];
  const items = (Array.isArray(rawItems) ? rawItems : [])
    .map(normalizeAdereItem)
    .filter(Boolean);

  const statusOrder = Array.isArray(body.statusOrder)
    ? body.statusOrder
    : (Array.isArray(body.statuses) ? body.statuses : null);

  return { items, statusOrder };
};

export const deriveAdereStatusOrder = (items, fallbackOrder) => {
  const keys = new Set();
  (items || []).forEach((item) => {
    Object.keys(item.counts || {}).forEach((status) => {
      if ((item.counts[status] ?? 0) > 0) keys.add(status);
    });
  });

  if (keys.size === 0) return fallbackOrder;

  const ordered = fallbackOrder.filter((status) => keys.has(status));
  keys.forEach((status) => {
    if (!ordered.includes(status)) ordered.push(status);
  });
  return ordered;
};

const parseAdereMonth = (value) => {
  if (!value) return 0;
  const [mm, yyyy] = String(value).split('/');
  const month = Number(mm);
  const year = Number(yyyy);
  if (!Number.isFinite(month) || !Number.isFinite(year)) return 0;
  return year * 100 + month;
};

export const formatAdereMonthLabel = (adereMonth) => {
  if (!adereMonth) return '—';
  return `Adere ${adereMonth}`;
};

const getMalhaKey = (item) => {
  if (item.malhaId) return String(item.malhaId);
  const nome = item.malhaNome || 'outras';
  return `malha-${nome.toLowerCase().replace(/\s+/g, '-')}`;
};

/** Nível 1: uma barra por mês, empilhada por status (soma de todas as malhas). */
export const buildAdereMonthlyOverviewModel = (items, statusOrder) => {
  const monthMap = new Map();

  (items || []).forEach((item) => {
    const month = item.adereMonth || '—';
    if (!monthMap.has(month)) {
      const row = {
        adereMonth: month,
        monthLabel: formatAdereMonthLabel(month),
      };
      statusOrder.forEach((status) => {
        row[status] = 0;
      });
      monthMap.set(month, row);
    }
    const row = monthMap.get(month);
    statusOrder.forEach((status) => {
      row[status] = (row[status] ?? 0) + (item.counts?.[status] ?? 0);
    });
  });

  const months = Array.from(monthMap.keys()).sort(
    (a, b) => parseAdereMonth(a) - parseAdereMonth(b),
  );
  const dataset = months.map((month) => monthMap.get(month));

  return { dataset, months };
};

/** Nível 2: barras por malha no mês selecionado, empilhadas por status. */
export const buildAdereMalhaDrilldownModel = (items, adereMonth, statusOrder) => {
  const monthItems = (items || []).filter((item) => item.adereMonth === adereMonth);
  const malhaMap = new Map();

  monthItems.forEach((item) => {
    const key = getMalhaKey(item);
    if (!malhaMap.has(key)) {
      malhaMap.set(key, {
        key,
        id: item.malhaId ?? null,
        nome: item.malhaNome || 'Outras',
      });
    }
  });

  const malhas = Array.from(malhaMap.values()).sort((a, b) =>
    a.nome.localeCompare(b.nome, 'pt-BR'),
  );

  const dataset = malhas.map((malha) => {
    const row = {
      malhaId: malha.id,
      malhaKey: malha.key,
      malhaLabel: malha.nome,
    };
    statusOrder.forEach((status) => {
      row[status] = 0;
    });
    return row;
  });

  const malhaIndex = Object.fromEntries(malhas.map((malha, index) => [malha.key, index]));

  monthItems.forEach((item) => {
    const rowIndex = malhaIndex[getMalhaKey(item)];
    if (rowIndex == null) return;
    statusOrder.forEach((status) => {
      dataset[rowIndex][status] = item.counts?.[status] ?? 0;
    });
  });

  return { dataset, malhas };
};

export const buildAdereStatusStackedSeries = (
  statusOrder,
  theme,
  getColorForStatus,
  getStatusLabel,
) =>
  statusOrder.map((status) => ({
    id: status,
    dataKey: status,
    stack: 'total',
    label: getStatusLabel(status),
    color: getColorForStatus(status, theme),
  }));

export const ChartEmpty = ({ message }) => (
  <Box
    sx={{
      height: CHART_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text.secondary',
      gap: 1,
    }}
  >
    <BarChartOutlinedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
    <Typography variant="body2">{message}</Typography>
  </Box>
);

ChartEmpty.propTypes = {
  message: PropTypes.string.isRequired,
};

export const ChartCard = ({ title, action, onExpand, contentSx, children }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
    <CardHeader
      title={title}
      titleTypographyProps={{ variant: 'h6' }}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {action}
          {onExpand && (
            <Tooltip title="Expandir">
              <IconButton size="small" onClick={onExpand}>
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      }
      sx={{ pb: 0 }}
    />
    <CardContent sx={{ pt: 1, pb: 2, ...contentSx }}>{children}</CardContent>
  </Card>
);

ChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  action: PropTypes.node,
  onExpand: PropTypes.func,
  contentSx: PropTypes.object,
  children: PropTypes.node.isRequired,
};

ChartCard.defaultProps = {
  action: null,
  onExpand: null,
  contentSx: null,
};

export const mapVencimentoToDataset = (items) =>
  (items || []).map((item) => ({
    dateLabel: item.dateLabel || item.date || '—',
    count: item.count ?? 0,
    bucket: item.bucket || 'later',
  }));

export const deriveVencimentoBucket = (dayMoment, todayMoment) => {
  const day = dayMoment.clone().startOf('day');
  const today = todayMoment.clone().startOf('day');
  const diff = day.diff(today, 'days');

  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff >= 1 && diff <= 7) return 'week';
  return 'later';
};

export const resolveDefaultVencimentoWindow = (mode, filterInicio, filterFim) => {
  const today = moment().startOf('day');

  if (mode === VENCIMENTO_MODES.FILTER_RANGE && filterInicio != null && filterFim != null) {
    let start = moment(filterInicio).startOf('day');
    let end = moment(filterFim).startOf('day');

    if (end.diff(start, 'days') < 7) {
      start = moment.min(start, today.clone().subtract(3, 'days'));
      end = moment.max(end, today.clone().add(3, 'days'));
    }

    return {
      start: start.format(ISO_DATE),
      end: end.format(ISO_DATE),
      today: today.format(ISO_DATE),
    };
  }

  return {
    start: today.clone().subtract(7, 'days').format(ISO_DATE),
    end: today.clone().add(7, 'days').format(ISO_DATE),
    today: today.format(ISO_DATE),
  };
};

const normalizeVencimentoWindow = (window, mode, filterInicio, filterFim) => {
  if (window?.start && window?.end) {
    return {
      start: moment(window.start).format(ISO_DATE),
      end: moment(window.end).format(ISO_DATE),
      today: moment(window.today || undefined).format(ISO_DATE),
    };
  }
  return resolveDefaultVencimentoWindow(mode, filterInicio, filterFim);
};

const resolveItemDateKey = (item, window) => {
  if (item?.date) {
    return moment(item.date).format(ISO_DATE);
  }

  if (!item?.dateLabel || !window?.start) return null;

  const startYear = moment(window.start).year();
  const endYear = moment(window.end).year();
  const [dd, mm] = String(item.dateLabel).split('/');

  if (!dd || !mm) return null;

  const candidates = [startYear];
  if (endYear !== startYear) candidates.push(endYear);

  const match = candidates
    .map((year) => moment(`${dd}/${mm}/${year}`, 'DD/MM/YYYY', true))
    .find((candidate) => candidate.isValid());

  return match ? match.format(ISO_DATE) : null;
};

/** Normaliza payload do endpoint vencimento-timeline (com fallback de janela no client). */
export const normalizeVencimentoChartResponse = (payload, mode, filterInicio, filterFim) => {
  const data = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
  const window = normalizeVencimentoWindow(data?.window, mode, filterInicio, filterFim);

  return {
    items: Array.isArray(data?.items) ? data.items : [],
    summary: data?.summary ?? null,
    window,
    alerts: Array.isArray(data?.alerts) ? data.alerts : [],
  };
};

export const buildVencimentoTimelineModel = (items, window) => {
  if (!window?.start || !window?.end) {
    return { dataset: [], todayDateLabel: null };
  }

  const todayMoment = moment(window.today || undefined).startOf('day');
  const start = moment(window.start).startOf('day');
  const end = moment(window.end).startOf('day');
  const itemMap = new Map();
  const labelMap = new Map();

  (items || []).forEach((item) => {
    const key = resolveItemDateKey(item, window);
    if (key) {
      itemMap.set(key, item);
    }
    if (item?.dateLabel) {
      labelMap.set(item.dateLabel, item);
    }
  });

  const dataset = [];
  let todayDateLabel = null;
  const cursor = start.clone();

  while (cursor.isSameOrBefore(end, 'day')) {
    const dateKey = cursor.format(ISO_DATE);
    const dateLabel = cursor.format('DD/MM');
    const existing = itemMap.get(dateKey) || labelMap.get(dateLabel);
    const bucket = existing?.bucket || deriveVencimentoBucket(cursor, todayMoment);
    const count = existing?.count ?? 0;

    if (cursor.isSame(todayMoment, 'day')) {
      todayDateLabel = dateLabel;
    }

    dataset.push({
      date: dateKey,
      dateLabel,
      count,
      bucket,
    });

    cursor.add(1, 'day');
  }

  return { dataset, todayDateLabel };
};

export const buildVencimentoColorMap = (dataset, theme) => ({
  type: 'ordinal',
  values: dataset.map((row) => row.dateLabel),
  colors: dataset.map((row) => resolveVencimentoBarColor(row.bucket, theme)),
});

export const formatVencimentoYAxisTick = (value) => {
  if (value == null || !Number.isFinite(value)) return '';
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) > 0.001) return '';
  if (rounded < 0) return '';
  return String(rounded);
};

export const hasVencimentoChartData = (summary, items) => {
  const totals =
    (summary?.vencidas ?? 0)
    + (summary?.vencemHoje ?? 0)
    + (summary?.vencemEm7Dias ?? 0);

  if (totals > 0) return true;
  return (items || []).some((item) => (item.count ?? 0) > 0);
};

export const resolveVencimentoBarColor = (bucket, theme) => {
  switch (bucket) {
    case 'overdue':
      return theme.palette.error.main;
    case 'today':
      return theme.palette.warning.dark;
    case 'week':
      return theme.palette.warning.main;
    default:
      return theme.palette.success.main;
  }
};
